import { computed, defineComponent, onBeforeUnmount, onMounted, reactive, ref, useContext, useCssModule, } from '@nuxtjs/composition-api';
export default defineComponent({
    name: 'ArticleAuthors',
    props: {
        article: {
            default: () => ({}),
            type: Object,
        },
    },
    setup(props) {
        /**
         * Authors.
         */
        const authors = computed(() => props.article.authors || []);
        /**
         * Context.
         */
        const context = useContext();
        /**
         * Authors hidden.
         */
        const authorsHidden = ref([]);
        /**
         * CSS Modules.
         */
        const css = useCssModule();
        /**
         * List element.
         */
        const listElement = ref();
        /**
         * List resize observer.
         */
        const listObserver = ref();
        /**
         * More element.
         */
        const moreElement = ref();
        /**
         * Root element.
         */
        const rootElement = ref();
        /**
         * Viewport.
         */
        const viewport = context.$viewport;
        /**
         * Tooltip state.
         */
        const tooltip = reactive({
            isActive: false,
            isVisible: computed(() => authorsHidden.value.length),
            items: computed(() => authorsHidden.value.map((key) => authors.value[key])),
        });
        /**
         * Handles window "click" event.
         */
        function handleWindowClick(event) {
            const isInside = moreElement.value?.contains(event.target);
            if (isInside) {
                return;
            }
            tooltip.isActive = false;
        }
        /**
         * Handles window "keyup" event.
         */
        function handleKeyUp(event) {
            if (event.code !== 'Escape') {
                return;
            }
            tooltip.isActive = false;
        }
        onBeforeUnmount(() => {
            listObserver.value?.disconnect();
            window.removeEventListener('click', handleWindowClick);
            window.removeEventListener('keyup', handleKeyUp);
        });
        onMounted(async () => {
            // Ignore if less, than two.
            if (authors.value.length < 2) {
                return;
            }
            // Find the root element.
            if (!rootElement.value) {
                return;
            }
            // Find the list parent element.
            if (!listElement.value) {
                return;
            }
            await new Promise((resolve) => setTimeout(resolve));
            // Find the parent scrollable.
            const scrollableArea = listElement.value.firstChild;
            if (!scrollableArea) {
                return;
            }
            // Get scrollable area children.
            const areaChildren = Array.from(scrollableArea.children || []);
            // Map children offsets.
            const childrenOffsets = areaChildren.map((child) => child.offsetWidth + child.offsetLeft);
            listObserver.value = new ResizeObserver(() => {
                // Ignore less than tablet viewport.
                if (viewport.isLessThan('tablet')) {
                    if (!authorsHidden.value.length) {
                        return;
                    }
                    // Remove all styles.
                    for (const child of areaChildren) {
                        child.removeAttribute('style');
                    }
                    // Remove all hidden authors.
                    authorsHidden.value = [];
                    return;
                }
                // Get the root width.
                const rectWidth = Math.floor(rootElement.value?.offsetWidth || 0);
                // Loop over children.
                let i = areaChildren.length;
                while (i--) {
                    const child = areaChildren[i];
                    const childOffset = childrenOffsets[i];
                    if (rectWidth < childOffset) {
                        child.setAttribute('style', 'display: none');
                        if (authorsHidden.value.includes(i)) {
                            continue;
                        }
                        authorsHidden.value.unshift(i);
                    }
                    else {
                        child.removeAttribute('style');
                        const index = authorsHidden.value.findIndex((value) => value === i);
                        if (index === -1) {
                            continue;
                        }
                        authorsHidden.value.splice(index, 1);
                    }
                }
            });
            listObserver.value.observe(rootElement.value);
            window.addEventListener('click', handleWindowClick);
            window.addEventListener('keyup', handleKeyUp);
        });
        return {
            authors,
            authorsHidden,
            css,
            listElement,
            moreElement,
            rootElement,
            tooltip,
        };
    },
});
